import { matchVocabAnswer, shuffleDeterministic, type GradeButton } from "@rmnlng/shared";
import { BookMarked, Check, CheckCheck, Pencil, RefreshCw, Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { CardImage } from "@/components/CardImage";
import { PlayAudioButton } from "@/components/PlayAudioButton";
import { QuizCardSurface, useQuizSubmitFlow } from "@/components/QuizCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useRomanianAudio } from "@/lib/audio";
import { useT, type I18nKey } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import {
  formatInterval,
  useMarkKnown,
  useReviewQueue,
  useSubmitReview,
  type GrammarReviewCard,
  type MarkKnownInput,
  type QuizReviewCard,
  type ReviewCard,
  type ReviewSubmitInput,
  type WordReviewCard,
} from "@/lib/reviews";

// Grade buttons: keep translation keys (not resolved strings) so the row
// re-renders when the language flips via the i18n context.
const GRADES: Array<{
  key: GradeButton;
  labelKey: I18nKey;
  shortcut: string;
  cls: string;
}> = [
  { key: "again", labelKey: "study.grade_again", shortcut: "1", cls: "sm2-btn again" },
  { key: "hard", labelKey: "study.grade_hard", shortcut: "2", cls: "sm2-btn hard" },
  { key: "good", labelKey: "study.grade_good", shortcut: "3", cls: "sm2-btn good" },
  { key: "easy", labelKey: "study.grade_easy", shortcut: "4", cls: "sm2-btn easy" },
];

const GENDER_LABEL: Record<string, string> = {
  M: "masc.",
  F: "fem.",
  N: "neut.",
};

type CardInteraction = {
  revealed: boolean;
  typedAnswer: string;
  typeChecked: boolean;
  mcqSelected: string | null;
};

const INITIAL_INTERACTION: CardInteraction = {
  revealed: false,
  typedAnswer: "",
  typeChecked: false,
  mcqSelected: null,
};

export function StudyPage() {
  const [searchParams] = useSearchParams();
  // Two session flavors since the words/grammar split: `?mode=grammar`
  // runs rules + their quizzes, anything else (default) runs vocabulary.
  // `?grammar=1` is the pre-split legacy param — old links keep working.
  const isGrammarSession = searchParams.get("mode") === "grammar" || searchParams.get("grammar") === "1";
  const queue = useReviewQueue({ kind: isGrammarSession ? "GRAMMAR" : "WORD" });
  const submit = useSubmitReview();
  const markKnown = useMarkKnown();
  const navigate = useNavigate();
  const t = useT();
  const [interaction, setInteraction] = useState<CardInteraction>(INITIAL_INTERACTION);
  // Two-tap confirmation for "I already know this" — first tap arms, second
  // commits. Cheap guard against misclicks; a misfire would ship the card
  // ~4 months into the future with no undo.
  const [knowArmed, setKnowArmed] = useState(false);
  const initialTotalRef = useRef<number | null>(null);
  // Timestamp when the current card first appeared on screen. Used to
  // compute `secondsSpent` on grade submission. Ref (not state) because
  // reading it shouldn't trigger a rerender, and the only consumer is the
  // grade handler at tap time.
  const cardStartRef = useRef<number>(0);

  const card: ReviewCard | undefined = useMemo(() => queue.data?.[0], [queue.data]);

  // Reset interaction state *during render* whenever the card key changes
  // — NOT in a useEffect. Here's why:
  //
  //   Effects run after commit. If we reset in a useEffect, there is exactly
  //   one committed frame where `card` already points to the new card but
  //   `interaction.revealed` still holds the previous card's "true" value.
  //   That frame renders the NEW lemma + NEW translations in blue (from
  //   RevealedAnswer) for ~16ms, then the effect fires and snaps to the
  //   unrevealed state. Visually: lemma flickers, layout shifts, blue
  //   answer "disappears" — the reported bug.
  //
  //   Setting state during render is a documented React pattern for
  //   "derive state from props": React detects the setState, discards the
  //   in-flight render, and re-runs before committing. The user never sees
  //   the stale frame.
  //
  // We guard on a ref so this only fires when the card key ACTUALLY changes;
  // otherwise we'd infinite-loop on every render.
  const lastCardKeyRef = useRef<string | null>(null);
  const currentCardKey = cardKey(card);
  if (lastCardKeyRef.current !== currentCardKey) {
    lastCardKeyRef.current = currentCardKey;
    setInteraction(INITIAL_INTERACTION);
    // Reset the confirm state whenever the card changes, so a stray "armed"
    // state doesn't carry across cards.
    setKnowArmed(false);
    // Start the per-card timer when a new card appears. `performance.now()`
    // is monotonic — not affected by wall-clock jumps (NTP sync, etc).
    cardStartRef.current = performance.now();
  }

  // Track the session's queue size so the top-bar counter can count up
  // (1/N → N/N). Monotonic — we only ever grow the ref, never shrink it.
  //
  // Why grow? Consider: user reviews 4 cards as "Again", gets to 5/15, then
  // exits. Those 4 cards stay due on the backend (SRS "Again" schedules them
  // for a few minutes out). When the user returns, `useReviewQueue` is
  // `refetchOnMount: "always"` — it first serves the optimistically-filtered
  // cache (11 cards), then refetches and gets 15 back. If we captured the
  // total as 11 on the first render and never updated it, we'd compute
  // `current = 11 − 15 + 1 = −3` once the refetch lands. Grow-only fixes it.
  //
  // Doing this during render (rather than in a `useEffect`) avoids a flash
  // of the stale total on the transition frame. Idempotent mutations to
  // refs during render are an allowed pattern.
  if (queue.data) {
    const len = queue.data.length;
    if (len === 0) {
      initialTotalRef.current = null;
    } else if (initialTotalRef.current === null || len > initialTotalRef.current) {
      initialTotalRef.current = len;
    }
  }

  const handleGrade = useCallback(
    (grade: GradeButton) => {
      if (!card || submit.isPending) return;
      // QUIZ cards have their own auto-grade flow inside <QuizCardView>;
      // the SM-2 grade row + keyboard shortcuts are hidden for them.
      // This early-return is defensive — none of the call sites should
      // reach here with a QUIZ card today, but the guard makes the
      // contract explicit.
      if (card.kind === "QUIZ") return;
      // `max(0,…)` guards against a freshly-mounted card firing instantly
      // (which would produce a tiny negative from floating-point noise).
      const secondsSpent = Math.max(0, Math.round((performance.now() - cardStartRef.current) / 1000));
      const input: ReviewSubmitInput =
        card.kind === "WORD"
          ? {
              kind: "WORD",
              wordId: card.wordId,
              direction: card.direction,
              grade,
              secondsSpent,
            }
          : { kind: "GRAMMAR", ruleId: card.ruleId, grade, secondsSpent };
      submit.mutate(input);
    },
    [card, submit],
  );

  const handleMarkKnown = useCallback(() => {
    if (!card || markKnown.isPending) return;
    // Same QUIZ guard as handleGrade — quizzes don't have a "Mark
    // known" affordance (the quiz IS the test of knowledge).
    if (card.kind === "QUIZ") return;
    if (!knowArmed) {
      setKnowArmed(true);
      return;
    }
    const input: MarkKnownInput =
      card.kind === "WORD"
        ? {
            kind: "WORD",
            wordId: card.wordId,
            direction: card.direction,
          }
        : { kind: "GRAMMAR", ruleId: card.ruleId };
    markKnown.mutate(input);
  }, [card, knowArmed, markKnown]);

  const isAnswered = answeredFor(card, interaction);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!card) return;
      // Don't hijack Space/Enter while typing in the input
      const typing = document.activeElement?.tagName === "INPUT";

      if (card.kind === "WORD" && card.mode === "flip" && !typing) {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          if (!interaction.revealed) setInteraction((i) => ({ ...i, revealed: true }));
          return;
        }
      }
      if (!isAnswered) return;
      const grade = GRADES.find((g) => g.shortcut === e.key);
      if (grade) {
        e.preventDefault();
        handleGrade(grade.key);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [card, interaction, isAnswered, handleGrade]);

  if (queue.isLoading) {
    return <div className="flex min-h-dvh items-center justify-center text-sm text-text-3">{t("study.loading")}</div>;
  }

  if (queue.isError) {
    return (
      <p className="text-sm text-[color:var(--again)]">
        {queue.error instanceof Error ? queue.error.message : t("common.failed_to_load")}
      </p>
    );
  }

  if (!card) {
    return <AllCaughtUp onRefresh={() => queue.refetch()} onExit={() => navigate("/")} />;
  }

  const total = initialTotalRef.current ?? queue.data!.length;
  const remaining = queue.data!.length;
  const current = total - remaining + 1;
  const progressPct = total > 0 ? (total - remaining) / total : 0;

  return (
    <div className="flex min-h-[calc(100dvh-40px)] flex-col gap-3.5">
      <SessionTopBar progress={progressPct} current={current} total={total} onExit={() => navigate("/")} />

      {card.kind === "WORD" ? (
        <WordCardView card={card} interaction={interaction} setInteraction={setInteraction} />
      ) : card.kind === "GRAMMAR" ? (
        <GrammarCardView card={card} />
      ) : (
        // `key` forces a fresh QuizCardView mount per card — local
        // state (selectedOption / typedInput / result) resets cleanly
        // without us threading a reset effect through.
        <QuizCardView key={card.quizItemId} card={card} />
      )}

      {/* Quiz cards have their own auto-grading flow (Phase 3a renderer
          will handle that). For now they short-circuit the SM-2 grade
          row + Know action — no manual grade buttons make sense for an
          auto-graded card. */}
      {card.kind === "QUIZ" ? null : isAnswered ? (
        <div className="sm2-row grid grid-cols-4 gap-2">
          {GRADES.map((g) => (
            <button
              key={g.key}
              type="button"
              className={g.cls}
              onClick={() => handleGrade(g.key)}
              disabled={submit.isPending}
            >
              <span className="label">{t(g.labelKey)}</span>
              <span className="interval">{formatInterval(card.preview[g.key])}</span>
            </button>
          ))}
        </div>
      ) : (
        <PrimaryAction card={card} interaction={interaction} setInteraction={setInteraction} />
      )}

      {card.kind !== "QUIZ" && (
        <KnowAction
          card={card}
          interaction={interaction}
          armed={knowArmed}
          pending={markKnown.isPending}
          onActivate={handleMarkKnown}
          onCancel={() => setKnowArmed(false)}
        />
      )}
    </div>
  );
}

// Tiny wrapper around the shared <QuizCardSurface>. Pulls in the
// SRS-aware flow hook so the dispatch line in StudyPage above can
// stay agnostic about flow vs. presentation. PracticeSession.tsx
// renders the surface directly with `usePracticeFlow`.
function QuizCardView({ card }: { card: QuizReviewCard }) {
  const flow = useQuizSubmitFlow(card);
  return <QuizCardSurface card={card} flow={flow} />;
}

function cardKey(card: ReviewCard | undefined): string {
  if (!card) return "";
  if (card.kind === "WORD") return `w:${card.wordId}:${card.direction}`;
  if (card.kind === "GRAMMAR") return `g:${card.ruleId}`;
  return `q:${card.quizItemId}`;
}

function answeredFor(card: ReviewCard | undefined, interaction: CardInteraction): boolean {
  if (!card) return false;
  if (card.kind === "GRAMMAR") return true;
  // Quiz cards use their own answer flow (Phase 3a) — they don't
  // surface the SM-2 grade row, so this branch is informational only
  // and the actual completion state lives in the QuizCardView's local
  // state when it ships.
  if (card.kind === "QUIZ") return false;
  if (card.mode === "type") return interaction.typeChecked;
  if (card.mode === "mcq") return interaction.mcqSelected !== null;
  return interaction.revealed;
}

function PrimaryAction({
  card,
  interaction,
  setInteraction,
}: {
  card: ReviewCard;
  interaction: CardInteraction;
  setInteraction: React.Dispatch<React.SetStateAction<CardInteraction>>;
}) {
  const t = useT();
  if (card.kind !== "WORD") return null;
  if (card.mode === "flip") {
    return (
      <Button variant="secondary" size="block" onClick={() => setInteraction((i) => ({ ...i, revealed: true }))}>
        {t("study.reveal")}
      </Button>
    );
  }
  if (card.mode === "type") {
    return (
      <Button
        size="block"
        disabled={!interaction.typedAnswer.trim()}
        onClick={() => setInteraction((i) => ({ ...i, typeChecked: true }))}
      >
        <Check className="size-4" />
        {t("study.check")}
      </Button>
    );
  }
  // MCQ: no primary button — options act as submission
  return null;
}

/* ─── "I already know this" action ────────────────────── */
// Two-tap confirmation (arm → commit). When armed, we render a small inline
// "Confirm · Cancel" pair instead of a modal — matches the rest of the Study
// flow (keyboard-first, no overlays) and makes the cost of a slip explicit.
function KnowAction({
  card,
  interaction,
  armed,
  pending,
  onActivate,
  onCancel,
}: {
  card: ReviewCard;
  interaction: CardInteraction;
  armed: boolean;
  pending: boolean;
  onActivate: () => void;
  onCancel: () => void;
}) {
  const t = useT();

  // Visibility + label rules:
  //
  // - For VOCAB in flip (reveal-only) mode: hide until the user has flipped
  //   the card. Pre-reveal "Mark known" is a leap of faith — the user
  //   doesn't know which translations they're committing to know. Post-
  //   reveal they've seen all senses and can rate honestly.
  //
  // - For VOCAB in mcq/type mode: keep visible — the affordance is
  //   "skip the test, I already know it cold." Cheap escape hatch.
  //
  // - For GRAMMAR: keep visible (no reveal-flip exists for rules — the
  //   body is rendered immediately). Label changes from "Я знаю слово" to
  //   "Я знаю тему" so the noun matches what the user is rating.
  if (card.kind === "WORD" && card.mode === "flip" && !interaction.revealed) {
    return null;
  }

  const labelKey = card.kind === "GRAMMAR" ? "study.know_action_grammar" : "study.know_action";

  if (armed) {
    return (
      <div className="flex items-center justify-center gap-3 pt-0.5 mb-2 text-[13px]">
        <span className="text-text-3">{t("study.know_armed_prompt")}</span>
        <button
          type="button"
          onClick={onActivate}
          disabled={pending}
          className="inline-flex items-center gap-1 font-semibold text-[color:var(--accent)] transition-opacity hover:opacity-80 disabled:opacity-50"
        >
          <CheckCheck className="size-3.5" />
          {pending ? t("study.know_saving") : t("common.yes")}
        </button>
        <button type="button" onClick={onCancel} className="text-text-3 hover:text-text">
          {t("common.cancel")}
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onActivate}
      className="self-center pt-0.5 mb-2 text-[13px] text-text-3 underline-offset-2 transition-colors hover:text-text hover:underline"
    >
      {t(labelKey)}
    </button>
  );
}

/* ─── Word card dispatcher ────────────────────────────── */

function WordCardView({
  card,
  interaction,
  setInteraction,
}: {
  card: WordReviewCard;
  interaction: CardInteraction;
  setInteraction: React.Dispatch<React.SetStateAction<CardInteraction>>;
}) {
  // The Romanian word's visibility depends on direction + interaction state.
  // Auto-play fires the moment the word becomes visible — no explicit user
  // action needed.
  //
  //   RO → RU: lemma is in the prompt and visible from the start.
  //   RU → RO: lemma is the answer and only visible after reveal/check/select.
  //
  // We pass the result both to `useRomanianAudio` (so it auto-plays on
  // change) and down to child components (so they know whether to render
  // the manual replay button).
  const visibleRomanian = getVisibleRomanian(card, interaction);
  const { play } = useRomanianAudio(visibleRomanian);

  if (card.mode === "type") {
    return (
      <TypeCardView
        card={card}
        interaction={interaction}
        setInteraction={setInteraction}
        playRomanian={visibleRomanian ? play : null}
      />
    );
  }
  if (card.mode === "mcq") {
    return (
      <MCQCardView
        card={card}
        interaction={interaction}
        setInteraction={setInteraction}
        playRomanian={visibleRomanian ? play : null}
      />
    );
  }
  return (
    <FlipCardView
      card={card}
      interaction={interaction}
      setInteraction={setInteraction}
      playRomanian={visibleRomanian ? play : null}
    />
  );
}

/**
 * Returns the Romanian text that the card is currently displaying, or null
 * if the Romanian word is hidden behind a "tap to reveal" affordance. The
 * caller uses this for both auto-play and the manual replay button.
 */
function getVisibleRomanian(card: WordReviewCard, interaction: CardInteraction): string | null {
  if (card.prompt.language === "RO") {
    return card.prompt.lemma;
  }
  // Direction is RU → RO — Romanian is in the answer, hidden until interaction.
  const answered =
    (card.mode === "flip" && interaction.revealed) ||
    (card.mode === "type" && interaction.typeChecked) ||
    (card.mode === "mcq" && interaction.mcqSelected !== null);
  if (!answered) return null;
  return card.answer.translations[0]?.lemma ?? null;
}

/* ─── Flip card ───────────────────────────────────────── */

function FlipCardView({
  card,
  interaction,
  setInteraction,
  playRomanian,
}: {
  card: WordReviewCard;
  interaction: CardInteraction;
  setInteraction: React.Dispatch<React.SetStateAction<CardInteraction>>;
  playRomanian: (() => void) | null;
}) {
  const revealed = interaction.revealed;
  const t = useT();
  return (
    <div
      className={cn("flashcard flex-1", !revealed && "cursor-pointer")}
      onClick={() => !revealed && setInteraction((i) => ({ ...i, revealed: true }))}
    >
      <CardMeta card={card} />
      <CardImage
        imageUrl={card.answer.imageUrl}
        imageQuery={card.answer.imageQuery}
        attribution={card.answer.imageAttribution}
        alt={card.prompt.lemma}
      />
      <PromptBlock card={card} playRomanian={playRomanian} />

      {revealed ? (
        <RevealedAnswer card={card} playRomanian={playRomanian} />
      ) : (
        <div className="flex flex-1 items-center justify-center pt-1 text-[13px] text-text-3">
          {t("study.tap_or_space")}{" "}
          <kbd className="ml-1.5 rounded border border-border bg-[color:var(--surface-2)] px-1.5 py-0.5 font-mono text-[11px]">
            Space
          </kbd>
        </div>
      )}
    </div>
  );
}

/* ─── Type card ───────────────────────────────────────── */

function TypeCardView({
  card,
  interaction,
  setInteraction,
  playRomanian,
}: {
  card: WordReviewCard;
  interaction: CardInteraction;
  setInteraction: React.Dispatch<React.SetStateAction<CardInteraction>>;
  playRomanian: (() => void) | null;
}) {
  const accepted = card.answer.translations.map((tr) => tr.lemma);
  const correctness = gradeTypedAnswer(interaction.typedAnswer, accepted);
  const primary = accepted[0] ?? "";
  const t = useT();
  // Show the audio button next to the answer block when the answer is in
  // Romanian (RU → RO direction) and the user has just checked their typing.
  const showAnswerAudio = card.prompt.language === "RU" && interaction.typeChecked && playRomanian;
  // The answer is always in the opposite language from the prompt (RO↔RU):
  // prompt RU → type Romanian, prompt RO → type Russian. Passed as a BCP-47
  // `lang` hint on the input so the OS/keyboard offers the matching
  // dictionary + predictions (and, where supported, the right language).
  const answerLang = card.prompt.language === "RU" ? "ro" : "ru";

  return (
    <div className="flashcard flex-1">
      <div className="flex items-center justify-between">
        <Badge variant="accent" className="gap-1">
          <Pencil className="size-3" /> {t("study.badge_type")}
        </Badge>
        {promptMeta(card).length > 0 && <span className="text-[12px] text-text-3">{promptMeta(card).join(" · ")}</span>}
      </div>
      <CardImage
        imageUrl={card.answer.imageUrl}
        imageQuery={card.answer.imageQuery}
        attribution={card.answer.imageAttribution}
        alt={card.prompt.lemma}
      />
      <PromptBlock card={card} playRomanian={playRomanian} />

      {!interaction.typeChecked ? (
        <div className="flex flex-col gap-2 pt-1">
          <div className="text-center text-[11px] font-semibold uppercase tracking-[0.06em] text-text-3">
            {t("study.type_prompt")}
          </div>
          <Input
            autoFocus
            lang={answerLang}
            inputMode="text"
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            value={interaction.typedAnswer}
            onChange={(e) => setInteraction((i) => ({ ...i, typedAnswer: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && interaction.typedAnswer.trim()) {
                e.preventDefault();
                setInteraction((i) => ({ ...i, typeChecked: true }));
              }
            }}
            placeholder={t("study.type_placeholder")}
            className="text-center font-mono"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-3 pt-2">
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-text-3">
              {t("study.type_prompt")}
            </div>
            <TypedDiff typed={interaction.typedAnswer} correct={primary} correct2={accepted} />
          </div>
          {!correctness.correct && (
            <div>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-text-3">
                {t("study.type_correct")}
              </div>
              <div className="flex items-center justify-center gap-2 font-mono text-[17px] font-semibold text-[color:var(--accent)]">
                <span>{accepted.join(" / ")}</span>
                {showAnswerAudio && playRomanian && <PlayAudioButton play={playRomanian} />}
              </div>
            </div>
          )}
          {/* When the user typed the answer correctly, the correct-answer
              block above isn't rendered. Surface a standalone replay button
              so the learner can still hear what they got right. */}
          {showAnswerAudio && correctness.correct && playRomanian && (
            <div className="flex justify-center">
              <PlayAudioButton play={playRomanian} />
            </div>
          )}
          {card.answer.examples[0] && (
            <div className="border-t border-dashed border-border pt-2 text-[13px] italic text-text-2">
              {card.answer.examples[0].text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function gradeTypedAnswer(typed: string, accepted: string[]): { correct: boolean } {
  // Case-, accent-, and parenthetical-insensitive — «Платок» and «платок»
  // both match «платок (головной)». See `matchVocabAnswer` for the axes.
  return { correct: matchVocabAnswer(typed, accepted) };
}

function TypedDiff({ typed, correct, correct2 }: { typed: string; correct: string; correct2: string[] }) {
  const trimmed = typed.trim();
  // Same matcher as the verdict, so the green/red highlight can never
  // disagree with whether the answer was accepted.
  const isCorrect = matchVocabAnswer(typed, correct2);
  const t = useT();
  if (isCorrect) {
    return (
      <div className="rounded-md border-[1.5px] border-[color:var(--good)] bg-[color:var(--good-soft)] px-4 py-3 text-center font-mono text-[17px] font-semibold text-[color:var(--good)]">
        {trimmed}
      </div>
    );
  }
  let prefix = 0;
  while (
    prefix < trimmed.length &&
    prefix < correct.length &&
    trimmed.charCodeAt(prefix) === correct.charCodeAt(prefix)
  ) {
    prefix++;
  }
  const matched = trimmed.slice(0, prefix);
  const wrong = trimmed.slice(prefix);
  return (
    <div className="rounded-md border-[1.5px] border-[color:var(--again)] bg-[color:var(--again-soft)] px-4 py-3 text-center font-mono text-[17px] text-[color:var(--again)]">
      {matched && <span>{matched}</span>}
      {wrong && <span className="line-through decoration-2 opacity-70">{wrong}</span>}
      {!trimmed && <span className="opacity-50">{t("study.type_empty")}</span>}
    </div>
  );
}

/* ─── MCQ card ────────────────────────────────────────── */

function MCQCardView({
  card,
  interaction,
  setInteraction,
  playRomanian,
}: {
  card: WordReviewCard;
  interaction: CardInteraction;
  setInteraction: React.Dispatch<React.SetStateAction<CardInteraction>>;
  playRomanian: (() => void) | null;
}) {
  const correctAnswer = card.answer.translations[0]?.lemma ?? "";
  const options = useMemo(() => {
    const all = [correctAnswer, ...(card.mcqDistractors ?? [])];
    return shuffleDeterministic(all, card.wordId + card.direction);
  }, [card.wordId, card.direction, correctAnswer, card.mcqDistractors]);
  const t = useT();

  const selected = interaction.mcqSelected;

  return (
    <div className="flashcard flex-1">
      <div className="flex items-center justify-between">
        <Badge variant="accent">{t("study.mcq_pick")}</Badge>
        {promptMeta(card).length > 0 && <span className="text-[12px] text-text-3">{promptMeta(card).join(" · ")}</span>}
      </div>
      <CardImage
        imageUrl={card.answer.imageUrl}
        imageQuery={card.answer.imageQuery}
        attribution={card.answer.imageAttribution}
        alt={card.prompt.lemma}
      />
      <PromptBlock card={card} playRomanian={playRomanian} />

      <div className="flex flex-col gap-2 pt-2">
        {options.map((opt, i) => {
          const letter = String.fromCharCode(65 + i);
          const isCorrect = opt === correctAnswer;
          const isSelected = selected === opt;
          const status: "idle" | "correct" | "wrong" =
            selected === null ? "idle" : isCorrect ? "correct" : isSelected ? "wrong" : "idle";
          return (
            <button
              key={opt}
              type="button"
              disabled={selected !== null}
              onClick={() => setInteraction((s) => ({ ...s, mcqSelected: opt }))}
              className={cn(
                "flex items-center gap-3 rounded-[var(--r-md)] border-[1.5px] px-3.5 py-3 text-left text-[15px] font-medium transition-colors",
                status === "idle" && "border-border bg-surface hover:bg-[color:var(--surface-2)]",
                status === "correct" &&
                  "border-[color:var(--good)] bg-[color:var(--good-soft)] text-[color:var(--good)]",
                status === "wrong" &&
                  "border-[color:var(--again)] bg-[color:var(--again-soft)] text-[color:var(--again)]",
              )}
            >
              <span className="flex size-6 items-center justify-center rounded-full border-[1.5px] border-current text-[12px] font-semibold">
                {letter}
              </span>
              <span className="flex-1">{opt}</span>
              {status === "correct" && <Check className="size-4" />}
              {status === "wrong" && <X className="size-4" />}
            </button>
          );
        })}
      </div>
      {/* After the user picks an option, show the replay button beneath the
          list — the correct Romanian word is now visible (highlighted green
          above) and the auto-play has fired exactly once. The button lets
          the learner hear it again without rerunning the card. */}
      {selected !== null && playRomanian && (
        <div className="flex justify-center pt-2">
          <PlayAudioButton play={playRomanian} />
        </div>
      )}
    </div>
  );
}

/* ─── Shared word card bits ───────────────────────────── */

function promptMeta(card: WordReviewCard): string[] {
  const parts: string[] = [];
  if (card.prompt.pos) parts.push(card.prompt.pos.toLowerCase());
  if (card.prompt.gender && GENDER_LABEL[card.prompt.gender]) {
    parts.push(GENDER_LABEL[card.prompt.gender]!);
  }
  return parts;
}

function CardMeta({ card }: { card: WordReviewCard }) {
  const t = useT();
  const directionLabel = card.direction === "RO_TO_RU" ? t("study.direction_ro_to_ru") : t("study.direction_ru_to_ro");
  const meta = promptMeta(card);
  return (
    <div className="flex items-center justify-between">
      <Badge variant="accent">{directionLabel}</Badge>
      {meta.length > 0 && <span className="text-[12px] text-text-3">{meta.join(" · ")}</span>}
    </div>
  );
}

function PromptBlock({ card, playRomanian }: { card: WordReviewCard; playRomanian: (() => void) | null }) {
  // Replay button only when the prompt itself is Romanian (RO → RU). For
  // RU → RO the prompt is Russian and there's nothing to play here — the
  // button shows up later, next to the revealed answer.
  const showAudio = card.prompt.language === "RO" && playRomanian;
  return (
    <div className="py-1 text-center">
      <div className="flex items-center justify-center gap-3">
        <div className="text-[36px] font-bold leading-[1.1] tracking-[-0.02em]">{card.prompt.lemma}</div>
        {showAudio && playRomanian && <PlayAudioButton play={playRomanian} />}
      </div>
      {card.prompt.pronunciation && (
        <div className="mt-1 font-mono text-[13px] text-text-3">{card.prompt.pronunciation}</div>
      )}
    </div>
  );
}

function RevealedAnswer({ card, playRomanian }: { card: WordReviewCard; playRomanian: (() => void) | null }) {
  const t = useT();
  // The answer is in Romanian only when the prompt was Russian (RU → RO).
  // In RO → RU the answer is Russian — no audio here.
  const showAudio = card.prompt.language === "RU" && playRomanian;
  // Visual hierarchy on translations: primary big in accent, secondary
  // senses muted under "Also: …". The example sentence below illustrates
  // the *primary* meaning by authoring convention, so giving all
  // translations equal visual weight made polysemous words (e.g. `prost`
  // → плохой / глупый) feel disjointed — the learner had to guess which
  // sense the example was demonstrating. Mirrors the WordDetail
  // `<TranslationCard>` treatment so both surfaces read the same way.
  const primary = card.answer.translations[0];
  const rest = card.answer.translations.slice(1);
  return (
    <div className="border-t border-dashed border-border pt-3 text-center">
      <div className="flex items-center justify-center gap-3">
        <div className="text-[28px] font-semibold tracking-[-0.01em] text-[color:var(--accent)]">
          {primary?.lemma ?? ""}
        </div>
        {showAudio && playRomanian && <PlayAudioButton play={playRomanian} />}
      </div>
      {rest.length > 0 && (
        <div className="mt-1 text-[13px] text-text-3">
          {t("word_detail.also_prefix")} {rest.map((tr) => tr.lemma).join(", ")}
        </div>
      )}
      {card.answer.synonyms.length > 0 && (
        <div className="mt-1.5 flex flex-wrap justify-center gap-1 text-[11px]">
          <span className="text-text-3">{t("study.synonyms_short")}</span>
          {card.answer.synonyms.map((s, i) => (
            <span key={s.id} className="text-text-3">
              {s.lemma}
              {i < card.answer.synonyms.length - 1 && ","}
            </span>
          ))}
        </div>
      )}
      {card.answer.examples[0] && (
        <div className="mt-2 text-[13px] italic text-text-2">{card.answer.examples[0].text}</div>
      )}
    </div>
  );
}

function GrammarCardView({ card }: { card: GrammarReviewCard }) {
  const t = useT();
  return (
    <div className="flashcard flex-1 overflow-y-auto">
      <div className="flex items-center justify-between">
        <Badge variant="accent" className="gap-1.5">
          <BookMarked className="size-3" /> {t("study.badge_grammar")}
        </Badge>
        {card.isNew && (
          <Badge variant="default" className="gap-1">
            <Sparkles className="size-3" /> {t("study.badge_new")}
          </Badge>
        )}
      </div>
      <h2 className="text-[26px] font-bold leading-[1.15] tracking-[-0.02em]">{card.title}</h2>
      {card.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {card.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-[11px] text-text-2"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}
      <div className="md mt-1">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{card.body}</ReactMarkdown>
      </div>
    </div>
  );
}

function SessionTopBar({
  progress,
  current,
  total,
  onExit,
}: {
  progress: number;
  current: number;
  total: number;
  onExit: () => void;
}) {
  return (
    <div className="flex items-center gap-3 pt-1">
      <button
        type="button"
        aria-label="Exit session"
        onClick={onExit}
        className="text-text-2 transition-colors hover:text-text"
      >
        <X className="size-6" />
      </button>
      <div className="progress-track flex-1">
        <div className="fill" style={{ width: `${progress * 100}%` }} />
      </div>
      <div className="min-w-[3.25rem] text-right font-mono text-[12px] text-text-3">
        {current}/{total}
      </div>
    </div>
  );
}

function AllCaughtUp({ onRefresh, onExit }: { onRefresh: () => void; onExit: () => void }) {
  const t = useT();
  return (
    <div className="flex min-h-[calc(100dvh-120px)] flex-col items-center justify-center gap-4 text-center">
      <Sparkles className="size-8 text-[color:var(--accent)]" />
      <div>
        <h1 className="text-[22px] font-bold">{t("study.all_caught_up_title")}</h1>
        <p className="mt-1.5 text-[13px] leading-[1.5] text-text-3">{t("study.all_caught_up_body")}</p>
      </div>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={onRefresh}>
          <RefreshCw className="size-4" /> {t("study.refresh")}
        </Button>
        <Button asChild size="sm">
          <Link to="/" onClick={onExit}>
            {t("study.back_to_home")}
          </Link>
        </Button>
      </div>
    </div>
  );
}
